import { injectable } from "inversify";
import ReportRepository, { GetUserReportResponse } from "../../../domain/repositories/ReportRepository";
import { Either, right } from "fp-ts/lib/Either";
import ErrorEntity from "../../../domain/entities/ErrorEntity";
import { UserType } from "../../../domain/entities/UserEntity";
import ShiftEntity, { ShiftStatus } from "../../../domain/entities/ShiftEntity";
import StablishmentEntity from "../../../domain/entities/StablishmentEntity";
import UserEntity from "../../../domain/entities/UserEntity";
import ScheduleEntity, { ScheduleDay } from "../../../domain/entities/ScheduleEntity";

@injectable()
class ReportRepositoryFake implements ReportRepository {
    async getUserReport(_: {
        userId: String,
        role: UserType,
        startDate: Date,
        endDate: Date,
    }): Promise<Either<ErrorEntity, GetUserReportResponse>> {
        // Simular delay
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Crear datos fake para el reporte
        const fakeStablishment = new StablishmentEntity({
            id: "1",
            name: "Restaurante El Buen Sabor",
            address: "Calle 123 #45-67",
            latitude: 4.6097,
            longitude: -74.0817,
            phone: "+57 300 123 4567",
            email: "contacto@elbuensabor.com",
            type: "restaurant",
            managerName: "Juan Pérez",
            supervisor: {
                id: "supervisor-123",
                name: "Juan Pérez",
                phone: "+1-555-0100",
                username: "jperez",
                role: "supervisor" as any,
                document: "12345678",
                documentType: "DNI",
                email: "jperez@example.com"
            },
            schedules: []
        });

        const fakeUser: UserEntity = {
            id: _.userId.toString(),
            name: "María García",
            email: "maria.garcia@example.com",
            role: UserType.VALET,
            phone: "+57 300 987 6543",
            username: "maria.garcia",
            document: "12345678",
            documentType: "CC"
        };

        const fakeSchedule = new ScheduleEntity({
            id: "1",
            day: ScheduleDay.lunes,
            openTime: "08:00",
            closeTime: "16:00"
        });

        // Generar turnos fake entre las fechas
        const shifts: ShiftEntity[] = [];
        const diffTime = Math.abs(_.endDate.getTime() - _.startDate.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        for (let i = 0; i < Math.min(diffDays, 10); i++) {
            const shiftDate = new Date(_.startDate);
            shiftDate.setDate(shiftDate.getDate() + i);

            // Solo agregar turnos de lunes a viernes
            if (shiftDate.getDay() >= 1 && shiftDate.getDay() <= 5) {
                const shift = new ShiftEntity({
                    turnId: `turn_${i}`,
                    id: `shift_${i}`,
                    status: i % 3 === 0 ? ShiftStatus.FINISHED : ShiftStatus.CONFIRMED_SECOND,
                    startDate: shiftDate,
                    stablishment: fakeStablishment,
                    user: fakeUser,
                    schedule: fakeSchedule,
                    willAttend: true,
                    checkedInAt: i % 3 === 0 ? new Date(shiftDate.getTime() + 8 * 60 * 60 * 1000) : undefined,
                    checkedOutAt: i % 3 === 0 ? new Date(shiftDate.getTime() + 16 * 60 * 60 * 1000) : undefined,
                    endDate: new Date(shiftDate.getTime() + 16 * 60 * 60 * 1000),
                });
                shifts.push(shift);
            }
        }

        const assistedShifts = shifts.filter(s => s.status === ShiftStatus.FINISHED).length;
        const missedShifts = shifts.length - assistedShifts;
        const pendingShifts = shifts.filter(s => s.status !== ShiftStatus.FINISHED).length;
        const workedHours = assistedShifts * 8; // 8 horas por turno completado

        return right({
            workedHours,
            assistedShifts,
            pendingShifts,
            missedShifts,
            shifts
        });
    }
}

export default ReportRepositoryFake;